import Label from "@/components/sub-atom/Label";
import currency from "@/functions/currency";
import {
  addCarts,
  deleteCarts,
  getProduct,
  revalidateCustom,
} from "@/functions/databaseData";
import { useCheckBreakPoint } from "@/functions/usResponsive";
import useDelayedRender from "@/functions/useDelayedRender";
import useHitungDiskon from "@/functions/useHitungDiskon";
import { BlurImage, EmptyImage } from "@/public/images";
import {
  DeleteOutlineSvg,
  DeleteSolidSvg,
  MinusSolidSvg,
  PlusSolidSvg,
} from "@/public/svg";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useDebounce, useDebouncedCallback } from "use-debounce";

function Table({ datas, carts, calculateStock, setCalculateStock, session }) {
  const shouldRendered = useDelayedRender(100);
  const breakpoint = useCheckBreakPoint();
  const headList = ["Produk", "Harga Satuan", "Quantity", "Subtotal", "Aksi"];
  const [loading, setLoading] = useState(false);
  async function handleDeleteProductCart() {
    try {
      if (loading) return;
      setLoading(true);

      let data = {
        id: session?.user?.data?.cart_id,
        all: true,
      };

      await deleteCarts({ body: data, path: "/cart" });
      setCalculateStock({});
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    shouldRendered && (
      <div id="cart-list-produk">
        {datas.length > 0 && (
          <div className="w-full bg-shadesGON-50 mb-2 text-right flex justify-between p-4 items-center">
            <p className="text-button-small text-greyscaleGON-600">
              Kode : {carts.kode_order}
            </p>
            <p
              className={twMerge(
                "md:text-sm text-xs font-poppins text-greyscaleGON-400  transition-all select-none",
                loading
                  ? "cursor-not-allowed text-greyscaleGON-200"
                  : "cursor-pointer hover:underline hover:underline-offset-4 hover:text-errorGON-400 duration-300 "
              )}
              onClick={handleDeleteProductCart}
            >
              {loading ? "loading..." : "Hapus Keranjang"}
            </p>
          </div>
        )}
        {breakpoint == "ui-laptop" ? (
          <table className="table-auto w-full shadow-mediumGON">
            <thead className="bg-shadesGON-50 border-b-[1px] border-b-greyscaleGON-200">
              <tr>
                {headList?.map((item, index) => (
                  <th
                    key={index}
                    className={twMerge("p-4", index === 0 ? "w-32" : " w-32")}
                  >
                    <p className="capitalize p2-medium text-center text-greyscaleGON-700 max-w-full whitespace-nowrap overflow-hidden text-ellipsis">
                      {item}
                    </p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-shadesGON-50">
              {datas?.reverse()?.map(([variantId, variant], index) => {
                let showDivider = index + 1 !== datas?.length;
                return (
                  <Compo
                    session={session}
                    index={index}
                    key={index}
                    id={variant?.product}
                    variant={variant}
                    variantId={variantId}
                    showDivider={showDivider}
                    carts={carts}
                    type={1}
                    calculateStock={calculateStock}
                    setCalculateStock={setCalculateStock}
                  />
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="bg-shadesGON-50 w-full shadow-mediumGON flex flex-col gap-4">
            {datas?.reverse()?.map(([variantId, variant], index) => {
              let showDivider = index + 1 !== datas?.length;

              return (
                <Compo
                  session={session}
                  key={index}
                  index={index}
                  id={variant?.product}
                  variant={variant}
                  variantId={variantId}
                  showDivider={showDivider}
                  carts={carts}
                  type={2}
                  calculateStock={calculateStock}
                  setCalculateStock={setCalculateStock}
                />
              );
            })}
          </div>
        )}
      </div>
    )
  );
}

function Compo({
  id,
  variant,
  variantId,
  showDivider,
  carts,
  index,
  type = 1,
  setCalculateStock,
  session,
}) {
  const path = usePathname();
  const queryClient = useQueryClient();
  const [qty, setQty] = useState(Number(variant?.jumlah));

  const isQty = useMemo(() => qty, [qty]);

  const debounced = useDebouncedCallback(
    // function
    (value) => {
      setCartProducts(Number(value));
    },
    // delay in ms
    100
  );

  const [debouncedOther] = useDebounce(isQty, 500);

  const [loading, setLoading] = useState({});
  const [product, setProduct] = useState();
  let isVariantId = variantId?.split("_")?.[1];

  useEffect(() => {
    if (variant?.jumlah) setQty(Number(variant?.jumlah));
  }, [variant?.jumlah]);

  useEffect(() => {
    debounced(debouncedOther);
  }, [debouncedOther]);

  useEffect(() => {
    runGetProduct();
  }, [id, session?.user?.token]);

  const runGetProduct = useCallback(async () => {
    setLoading((old) => ({ ...old, get: true }));
    try {
      const data = await getProduct({
        id: id,
        token: session?.user?.token,
      });
      setProduct(data);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading((old) => ({ ...old, get: false }));
    }
  }, [id, session?.user?.token]);

  const isVariant = product?.variants?.filter(
    (item) => item?.variant_id == isVariantId
  )?.[0];

  const diskonRange = {
    status: product?.diskon_brand > 0 || product?.diskon_product > 0,
    total: product?.diskon_brand + product?.diskon_product,
  };

  // validasi max total diskon persen
  diskonRange.total = diskonRange.total >= 70 ? 70 : diskonRange.total;

  const diskonSale = {
    status:
      product?.diskon_brand_nominal > 0 || product?.diskon_product_nominal > 0,
    total: product?.diskon_brand_nominal + product?.diskon_product_nominal,
  };

  let checkUseDiskon = useHitungDiskon(product, isVariant?.price_tags);

  async function handleDeleteProductCart(item, id) {
    try {
      if (loading?.changeQty) return;
      setLoading((old) => ({ ...old, deleteCarts: true }));

      let data = {
        id: session?.user?.data?.cart_id,
        variantKey: id,
        total_pembayaran: checkUseDiskon,
        total_tagihan: checkUseDiskon,
        total_tagihan_normal: isVariant?.price_tags,
        weight: isVariant?.package_weight,
        qty: variant?.jumlah,
      };

      const res = await deleteCarts({ body: data, path });
      setCalculateStock((old) => ({
        ...old,
        [isVariant?.variant2_color]: false,
      }));
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading((old) => ({ ...old, deleteCarts: false }));
    }
  }

  useEffect(() => {
    runCalculate();
  }, [isVariant, isQty]);

  const runCalculate = useCallback(() => {
    setCalculateStock((old) => ({
      ...old,
      [isVariant?.variant2_color]: isQty > isVariant?.stock,
    }));
  }, [isVariant, isQty]);

  const setCartProducts = useCallback(
    async (nil) => {
      try {
        if (isVariant?.stock <= 0) return;
        if (loading?.deleteCarts) return;

        setLoading((old) => ({ ...old, changeQty: true }));

        if (nil <= 0 || nil > isVariant?.stock)
          return setCartProducts(nil <= 0 ? 1 : isVariant?.stok);

        const jumlahLama = variant?.jumlah;

        let jumlahBaru = nil - jumlahLama;

        let isQty = jumlahBaru > isVariant?.stock ? 0 : jumlahBaru;

        let data = {
          id: session?.user?.data?.cart_id,
          products: carts?.products,
          total_pembayaran: checkUseDiskon,
          total_tagihan: checkUseDiskon,
          total_tagihan_normal: isVariant?.price_tags,
          pengiriman: {},
          biaya_pengiriman: 0,
          total_tagihan_before: 0,
          pakai_asuransi: false,
          weight: isVariant?.package_weight ?? 1,
          length: isVariant?.package_length ?? 1,
          height: isVariant?.package_height ?? 1,
          width: isVariant?.package_width ?? 1,
          // opsional field
          variantKey: variantId,
          qty: isQty,
          maxQty: isVariant?.stock,
        };

        try {
          const res = await addCarts({ body: data });
          // setQty(res?.products[variantId]?.jumlah);
          queryClient.setQueryData(["carts"], (old) => res);
          console.log(`Item added to cart successfully. ${isQty}`);
        } catch (error) {
          console.log("Error adding item to cart:", error);
        }
      } catch (error) {
        console.log("error jumlah = ", error);
      } finally {
        setLoading((old) => ({ ...old, changeQty: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isVariant,
      variant?.jumlah,
      carts?.products,
      session?.user?.data?.mitra_id,
      checkUseDiskon,
    ]
  );

  if (!product && !isVariant)
    return type == 1 ? (
      <tr>
        <td>
          <div>...</div>
        </td>
      </tr>
    ) : (
      <div>...</div>
    );

  return type == 1 ? (
    <tr
      key={index}
      className={twMerge(
        "border-b-[1px] ",
        showDivider ? "border-b-greyscaleGON-200" : "border-b-transparent",
        isVariant?.stock <= 0
          ? "select-none bg-errorGON-100/50 cursor-not-allowed pointer-events-none"
          : ""
      )}
    >
      <td className="p-4 flex items-center">
        <div className="flex gap-4 ">
          <div>
            {!isVariant?.thumbnails ||
            isVariant?.thumbnails === '""' ||
            isVariant?.thumbnails === '"' ||
            isVariant?.thumbnails === "" ||
            isVariant?.thumbnails == "-" ||
            !(
              isVariant?.thumbnails &&
              (isVariant?.thumbnails.startsWith("http") ||
                isVariant?.thumbnails.startsWith("/"))
            ) ? (
              <div className="w-24 h-24 bg-greyscaleGON-50 flex justify-center items-center">
                <Image
                  unoptimized
                  alt={`image_${index}`}
                  src={EmptyImage}
                  width={240}
                  height={240}
                  className="object-cover object-top w-auto h-auto max-w-24 max-h-24"
                  blurDataURL={BlurImage?.blurDataURL}
                  placeholder="blur"
                />
              </div>
            ) : (
              <div className="w-24 h-24 overflow-hidden">
                <Image
                  alt="image_1"
                  src={isVariant?.thumbnails}
                  width={240}
                  height={240}
                  className="object-cover object-top w-auto h-auto"
                  blurDataURL={BlurImage?.blurDataURL}
                  placeholder="blur"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 max-w-full overflow-hidden text-ellipsis">
            <p className="p1-semiBold text-greyscaleGON-800">
              {product?.name_product}
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex gap-4">
                <p className="p2-regular min-w-[50px]   text-greyscaleGON-400">
                  Variant
                </p>
                <p className="p2-medium   text-primaryGON-400">
                  {isVariant?.variant2_color}
                </p>
              </div>
              <div className="flex gap-4">
                <p className="p2-regular min-w-[50px]   text-greyscaleGON-400">
                  Size
                </p>
                <p className="p2-medium   text-primaryGON-400">
                  {!isVariant?.variant1_size ? "-" : isVariant?.variant1_size}
                </p>
              </div>
              <div className="flex gap-4">
                <p className="p2-regular min-w-[50px]   text-greyscaleGON-400">
                  Berat
                </p>
                <p className="p2-medium   text-primaryGON-400">
                  {isVariant?.package_weight}
                </p>
              </div>
            </div>
          </div>
        </div>
      </td>
      <td className="text-center">
        <div className="flex flex-col gap-2 justify-center items-center">
          {(diskonRange?.status || diskonSale?.status) && (
            <div className="flex gap-1 items-center">
              <p className="p2-semiBold text-greyscaleGON-400 line-through">
                {currency(isVariant?.price_tags)}
              </p>
              {diskonRange?.status && (
                <Label title={`${diskonRange?.total}%`} colors="secondary" />
              )}
            </div>
          )}
          <div>
            <p className="p1-semiBold text-greyscaleGON-700">
              {currency(checkUseDiskon)}
            </p>
          </div>
        </div>
      </td>
      <td className="text-center">
        <div
          className={twMerge(
            "flex items-center justify-between p-2 rounded-lg border",
            isQty > isVariant?.stock
              ? "border-errorGON-100 mb-1"
              : "border-greyscaleGON-100"
          )}
        >
          <div
            onMouseDown={(e) => e.preventDefault()}
            className={twMerge(
              (isVariant?.stock <= 0 || isQty <= 1) &&
                "text-opacity-20 cursor-not-allowed",
              "text-primaryGON-400 cursor-pointer"
            )}
            onClick={() => {
              if (isVariant?.stock <= 0 || isQty <= 1) return;
              if (isQty > isVariant?.stock) {
                return setQty(isVariant?.stock);
              }
              if (isQty > 1) {
                setQty(isQty - 1 <= 0 ? 1 : isQty - 1);
              }
            }}
          >
            <MinusSolidSvg className=" h-7 w-7 xs:h-10 xs:w-10 " />
          </div>
          <input
            className={twMerge(
              isQty > isVariant?.stock
                ? "text-errorGON-100"
                : "text-greyscaleGON-700",
              "outline-none text-center w-[5ch]"
            )}
            placeholder="0"
            value={isVariant?.stock <= -1 ? 0 : isQty}
            disabled={isVariant?.stock <= 0}
            onChange={(val) => {
              const re = /^[0-9\b]+$/;
              if (val.target.value === "" || re.test(val.target.value)) {
                const value = val?.target?.value < -1 ? 0 : val?.target?.value;
                debounced(value);
              }
            }}
          />
          <div
            onMouseDown={(e) => e.preventDefault()}
            className={twMerge(
              (isVariant?.stock <= 0 || isQty >= isVariant?.stock) &&
                "text-opacity-20 cursor-not-allowed",
              "text-primaryGON-400 cursor-pointer"
            )}
            onClick={() => {
              if (isVariant?.stock <= 0 || isQty >= isVariant?.stock) return;

              if (isQty < isVariant?.stock) {
                setQty(isQty + 1);
              }
            }}
          >
            <PlusSolidSvg className="h-7 w-7 xs:h-10 xs:w-10" />
          </div>
        </div>
        {(isVariant?.stock <= 0 || isQty > isVariant?.stock || isQty <= 0) && (
          <div className="flex justify-end text-right">
            <p className="p2-medium text-errorGON-400">
              {isVariant?.stock <= 0
                ? "Item habis"
                : isQty <= 0
                ? `Minimal pembelian produk ini adalah 1 barang`
                : `Max. ${isVariant?.stock} item`}
            </p>
          </div>
        )}
      </td>
      <td className="text-center">
        <div className="flex items-center justify-center">
          <p className="capitalize p1-semiBold text-greyscaleGon-700">
            {currency(checkUseDiskon * variant?.jumlah)}
          </p>
        </div>
      </td>
      <td className="text-center">
        <div
          onClick={() => handleDeleteProductCart(carts, variantId)}
          className={twMerge(
            loading.changeQty
              ? "text-greyscaleGON-200 cursor-not-allowed"
              : "text-errorGON-400 cursor-pointer",
            "justify-center items-center  inline-block"
          )}
        >
          <DeleteSolidSvg className={twMerge("h-7 w-7")} />
        </div>
      </td>
    </tr>
  ) : (
    type == 2 && (
      <div
        key={index}
        className={twMerge(
          "flex gap-4 px-4 pb-4 border-b-[1px] ",
          index == 0 && "pt-4",
          showDivider ? "border-b-greyscaleGON-200" : "border-b-transparent",
          isVariant?.stock <= 0
            ? "select-none bg-errorGON-100/50 cursor-not-allowed pointer-events-none"
            : ""
        )}
      >
        <div className="flex flex-col gap-4 md:flex-row justify-between w-full md:items-end">
          <div className="flex gap-4 md:w-auto">
            <div>
              {!isVariant?.thumbnails ||
              isVariant?.thumbnails === '""' ||
              isVariant?.thumbnails === '"' ||
              isVariant?.thumbnails === "" ||
              isVariant?.thumbnails == "-" ||
              !(
                isVariant?.thumbnails &&
                (isVariant?.thumbnails.startsWith("http") ||
                  isVariant?.thumbnails.startsWith("/"))
              ) ? (
                <div className="w-24 h-24 bg-greyscaleGON-50 flex justify-center items-center">
                  <Image
                    unoptimized
                    alt={`image_${index}`}
                    src={EmptyImage}
                    width={240}
                    height={240}
                    className="object-cover object-top w-auto h-auto max-w-24 max-h-24"
                    placeholder="blur"
                    blurDataURL={BlurImage?.blurDataURL}
                  />
                </div>
              ) : (
                <div className="w-24 h-24 overflow-hidden">
                  <Image
                    alt={`image_${index}`}
                    src={isVariant?.thumbnails}
                    width={240}
                    height={240}
                    className="object-cover object-top w-auto h-auto"
                    placeholder="blur"
                    blurDataURL={BlurImage?.blurDataURL}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 max-w-full overflow-hidden text-ellipsis">
              <p className="p1-semiBold text-greyscaleGON-800">
                {product?.name_product}
              </p>
              <div className="flex flex-col gap-2">
                <p className="p2-medium   text-primaryGON-400">
                  {isVariant?.variant2_color},{" "}
                  {!isVariant?.variant1_size ? "-" : isVariant?.variant1_size},{" "}
                  {isVariant?.package_weight}
                </p>
              </div>
              <div className="flex flex-col items-start xs:flex-row xs:items-center gap-2">
                <div>
                  <p className="p1-semiBold text-greyscaleGON-700">
                    {currency(checkUseDiskon)}
                  </p>
                </div>
                {(diskonRange?.status || diskonSale?.status) && (
                  <div className="flex gap-1 items-center">
                    <p className="p2-semiBold text-greyscaleGON-400 line-through">
                      {currency(isVariant?.price_tags)}
                    </p>
                    {diskonRange?.status && (
                      <Label
                        title={`${diskonRange?.total}%`}
                        colors="secondary"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="md:w-1/4 flex justify-end items-end">
            <div className="flex flex-col">
              <div className="flex gap-4 items-center">
                <div
                  onClick={() => handleDeleteProductCart(carts, variantId)}
                  className={twMerge(
                    loading.changeQty
                      ? "text-greyscaleGON-200 cursor-not-allowed"
                      : "text-errorGON-400 cursor-pointer",
                    "flex justify-center items-center cursor-pointer"
                  )}
                >
                  <DeleteOutlineSvg className="h-7 w-7" />
                </div>
                <div
                  className={twMerge(
                    "w-fit flex items-center justify-between p-2 rounded-lg border",
                    isQty > isVariant?.stock
                      ? "border-errorGON-100 mb-1"
                      : "border-greyscaleGON-100"
                  )}
                >
                  <div
                    onMouseDown={(e) => e.preventDefault()}
                    className={twMerge(
                      (isVariant?.stock <= 0 || isQty <= 1) &&
                        "text-opacity-20 cursor-not-allowed",
                      "text-primaryGON-400 cursor-pointer"
                    )}
                    onClick={() => {
                      if (isVariant?.stock <= 0 || isQty <= 1) return;
                      if (isQty > isVariant?.stock) {
                        return setQty(isVariant?.stock);
                      }
                      if (isQty > 1) {
                        setQty(isQty - 1 <= 0 ? 1 : isQty - 1);
                      }
                    }}
                  >
                    <MinusSolidSvg className=" h-7 w-7 xs:h-10 xs:w-10 " />
                  </div>
                  <input
                    className={twMerge(
                      isQty > isVariant?.stock
                        ? "text-errorGON-100"
                        : "text-greyscaleGON-700",
                      "outline-none text-center w-[5ch]"
                    )}
                    placeholder="0"
                    disabled={isVariant?.stock <= 0}
                    value={isVariant?.stock <= -1 ? 0 : isQty}
                    onChange={(val) => {
                      const re = /^[0-9\b]+$/;
                      if (
                        val.target.value === "" ||
                        re.test(val.target.value)
                      ) {
                        const value =
                          val?.target?.value < -1 ? 0 : val?.target?.value;
                        debounced(value);
                      }
                    }}
                  />
                  <div
                    onMouseDown={(e) => e.preventDefault()}
                    className={twMerge(
                      (isVariant?.stock <= 0 || isQty >= isVariant?.stock) &&
                        "text-opacity-20 cursor-not-allowed",
                      "text-primaryGON-400 cursor-pointer"
                    )}
                    onClick={() => {
                      if (isVariant?.stock <= 0 || isQty >= isVariant?.stock)
                        return;
                      if (isQty < isVariant?.stock) {
                        setQty(isQty + 1);
                      }
                    }}
                  >
                    <PlusSolidSvg className="h-7 w-7 xs:h-10 xs:w-10" />
                  </div>
                </div>
              </div>
              {(isVariant?.stock <= 0 ||
                isQty > isVariant?.stock ||
                isQty <= 0) && (
                <div className="flex justify-end text-right">
                  <p className="p2-medium text-errorGON-400">
                    {isVariant?.stock <= 0
                      ? "Item habis"
                      : isQty <= 0
                      ? `Minimal pembelian produk ini adalah 1 barang`
                      : `Max. ${isVariant?.stock} item`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
}

export default memo(Table);
